import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiTrendingUp, FiTrendingDown, FiUsers, FiCheckCircle,
  FiClock, FiPercent, FiRefreshCw, FiCalendar, FiFilter,
  FiBarChart2, FiPieChart, FiMail, FiPhone, FiLinkedin,
  FiAlertTriangle, FiAward,
} from 'react-icons/fi';
import { API_URL, getValidToken } from '../../../utils/authUtils';

/**
 * BrokerageAnalyticsDashboard - Visual analytics dashboard for staff
 *
 * Features:
 * - Key performance metrics cards
 * - Conversion funnel visualization
 * - Requests by type/status breakdown
 * - Channel effectiveness comparison
 * - Staff performance overview
 * - Time-series trends
 *
 * Spec Reference: Phase 10 - Staff UI Polish
 */

// Date range presets
const DATE_RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
];

const BrokerageAnalyticsDashboard = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');

  // Data state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [staffPerformance, setStaffPerformance] = useState(null);
  const [channelEffectiveness, setChannelEffectiveness] = useState(null);
  const [declineReasons, setDeclineReasons] = useState(null);

  // Calculate date range
  const getDateRange = useCallback(() => {
    const now = new Date();
    let dateFrom = null;
    let dateTo = now.toISOString().split('T')[0];

    switch (dateRange) {
      case '7d':
        dateFrom = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '30d':
        dateFrom = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '90d':
        dateFrom = new Date(now - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'ytd':
        dateFrom = `${now.getFullYear()}-01-01`;
        break;
      case 'all':
        dateFrom = null;
        dateTo = null;
        break;
      default:
        dateFrom = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    return { dateFrom, dateTo };
  }, [dateRange]);

  // Fetch all analytics data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getValidToken();
      const headers = { Authorization: `Bearer ${token}` };
      const { dateFrom, dateTo } = getDateRange();

      // Build query params
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (requestTypeFilter) params.append('request_type', requestTypeFilter);

      const queryString = params.toString();
      const queryPart = queryString ? `?${queryString}` : '';

      // Fetch all data in parallel
      const [dashboardRes, funnelRes, performanceRes, channelRes, declineRes] = await Promise.all([
        axios.get(`${API_URL}/api/staff/brokerage/analytics/dashboard${queryPart}`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/analytics/funnel${queryPart}`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/analytics/performance${queryPart}`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/analytics/channel-effectiveness${queryPart}`, { headers }),
        axios.get(`${API_URL}/api/staff/brokerage/analytics/decline-reasons${queryPart}&limit=5`, { headers }),
      ]);

      setDashboardStats(dashboardRes.data);
      setFunnelData(funnelRes.data);
      setStaffPerformance(performanceRes.data);
      setChannelEffectiveness(channelRes.data);
      setDeclineReasons(declineRes.data);

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, requestTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  // Format number
  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString();
  };

  // Format days
  const formatDays = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)} days`;
  };

  // Get trend indicator
  const getTrendIndicator = (current, previous) => {
    if (!current || !previous) return null;
    const change = ((current - previous) / previous) * 100;
    if (change > 0) {
      return (
        <span className="flex items-center text-green-600 dark:text-green-400 text-sm">
          <FiTrendingUp className="w-4 h-4 mr-1" />
          +{change.toFixed(1)}%
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="flex items-center text-red-600 dark:text-red-400 text-sm">
          <FiTrendingDown className="w-4 h-4 mr-1" />
          {change.toFixed(1)}%
        </span>
      );
    }
    return null;
  };

  // Get channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <FiMail className="w-4 h-4" />;
      case 'linkedin': return <FiLinkedin className="w-4 h-4" />;
      case 'phone': return <FiPhone className="w-4 h-4" />;
      default: return <FiMail className="w-4 h-4" />;
    }
  };

  if (loading && !dashboardStats) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Brokerage Analytics
        </h2>
        <div className="flex items-center gap-3">
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <FiCalendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {DATE_RANGES.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>

          {/* Request Type Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <select
              value={requestTypeFilter}
              onChange={(e) => setRequestTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Types</option>
              <option value="MENTORSHIP_UP">Mentorship (Seek)</option>
              <option value="MENTORSHIP_HIGH">Mentorship (Offer)</option>
              <option value="PEER_MID_LEVEL">Peer Networking (Mid)</option>
              <option value="PEER_SENIOR">Peer Networking (Senior)</option>
              <option value="BOARD_PATHWAY">Board Pathway</option>
              <option value="HBCU_MENTORSHIP">HBCU Mentorship</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Submitted */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <FiUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatNumber(dashboardStats?.requests_submitted)}
          </div>
        </div>

        {/* Sent */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FiMail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Sent</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatNumber(dashboardStats?.requests_sent)}
          </div>
        </div>

        {/* Connected */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Connected</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatNumber(dashboardStats?.requests_connected)}
          </div>
        </div>

        {/* Acceptance Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <FiPercent className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Acceptance Rate</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatPercent(dashboardStats?.acceptance_rate)}
          </div>
        </div>

        {/* Avg Time to Connect */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
              <FiClock className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Time</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatDays(dashboardStats?.avg_time_to_connect)}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FiBarChart2 className="w-5 h-5 text-indigo-500" />
            Conversion Funnel
          </h3>
          {funnelData && (
            <div className="space-y-3">
              {/* Funnel Stages */}
              {[
                { key: 'submitted', label: 'SUBMITTED', color: 'bg-blue-500' },
                { key: 'approved', label: 'APPROVED', color: 'bg-indigo-500' },
                { key: 'sent', label: 'SENT', color: 'bg-purple-500' },
                { key: 'consented', label: 'CONSENTED', color: 'bg-pink-500' },
                { key: 'connected', label: 'CONNECTED', color: 'bg-green-500' },
              ].map((stage, index) => {
                const count = funnelData[`requests_${stage.key}`] || 0;
                const total = funnelData.requests_submitted || 1;
                const percentage = (count / total) * 100;

                return (
                  <div key={stage.key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">{stage.label}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {count} ({percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                      <div
                        className={`h-full ${stage.color} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    {index < 4 && funnelData.drop_off_rates && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">
                        Drop-off: {formatPercent(funnelData.drop_off_rates[stage.key])}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Requests by Type */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FiPieChart className="w-5 h-5 text-indigo-500" />
            Requests by Type
          </h3>
          {dashboardStats?.requests_by_type && (
            <div className="space-y-3">
              {Object.entries(dashboardStats.requests_by_type).map(([type, count]) => {
                const total = Object.values(dashboardStats.requests_by_type).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;

                const typeLabels = {
                  'MENTORSHIP_UP': 'Mentorship (Seek)',
                  'MENTORSHIP_HIGH': 'Mentorship (Offer)',
                  'PEER_MID_LEVEL': 'Peer (Mid-Level)',
                  'PEER_SENIOR': 'Peer (Senior)',
                  'BOARD_PATHWAY': 'Board Pathway',
                  'HBCU_MENTORSHIP': 'HBCU Mentorship',
                };

                const typeColors = {
                  'MENTORSHIP_UP': 'bg-blue-500',
                  'MENTORSHIP_HIGH': 'bg-indigo-500',
                  'PEER_MID_LEVEL': 'bg-purple-500',
                  'PEER_SENIOR': 'bg-pink-500',
                  'BOARD_PATHWAY': 'bg-amber-500',
                  'HBCU_MENTORSHIP': 'bg-green-500',
                };

                return (
                  <div key={type} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${typeColors[type] || 'bg-gray-400'}`} />
                    <span className="flex-1 text-sm text-gray-600 dark:text-gray-400">
                      {typeLabels[type] || type}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {count}
                    </span>
                    <div className="w-24 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${typeColors[type] || 'bg-gray-400'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Channel Effectiveness */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FiMail className="w-5 h-5 text-indigo-500" />
            Channel Effectiveness
          </h3>
          {channelEffectiveness?.channels && (
            <div className="space-y-4">
              {channelEffectiveness.channels.map((channel) => (
                <div key={channel.channel} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-24">
                    {getChannelIcon(channel.channel)}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {channel.channel}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500 dark:text-gray-400">
                        Response Rate
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatPercent(channel.response_rate)}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${(channel.response_rate || 0) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {channel.total_attempts} attempts
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Decline Reasons */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FiAlertTriangle className="w-5 h-5 text-indigo-500" />
            Top Decline Reasons
          </h3>
          {declineReasons?.reasons && declineReasons.reasons.length > 0 ? (
            <div className="space-y-3">
              {declineReasons.reasons.map((reason, index) => (
                <div key={reason.reason_code || index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-400">
                    {index + 1}
                  </div>
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                    {reason.reason_code?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {reason.count}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({formatPercent(reason.percentage)})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 py-4">
              No decline data available
            </div>
          )}
        </div>
      </div>

      {/* Staff Performance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <FiAward className="w-5 h-5 text-indigo-500" />
          Staff Performance
        </h3>
        {staffPerformance?.staff && staffPerformance.staff.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Closed
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Avg Time
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Accept %
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {staffPerformance.staff.map((staff) => (
                  <tr key={staff.staff_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {staff.staff_name || `Staff #${staff.staff_id}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                      {staff.total_assigned || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                      {staff.closed_successfully || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                      {staff.avg_time_to_close ? `${staff.avg_time_to_close.toFixed(1)}d` : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`font-medium ${
                        staff.acceptance_rate >= 0.5 ? 'text-green-600 dark:text-green-400' :
                        staff.acceptance_rate >= 0.3 ? 'text-amber-600 dark:text-amber-400' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {formatPercent(staff.acceptance_rate)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="flex items-center justify-center gap-1">
                        <span className="text-amber-500">★</span>
                        {staff.avg_feedback_score?.toFixed(1) || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            No staff performance data available
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerageAnalyticsDashboard;
